import { Util } from '@libs/utilities/util';
import { Stats } from '@libs/utilities/stats';

import { IAmazonCompositeKeywordData, IGoogleSearchVolumeValue } from '../../../../constants/research';
import { GoogleSvEvaluatorService } from './google-sv-evaluator.service';
import { SearchVolumeEvaluatorHelper } from './search-volume-evaluator.helper';
import { CompetitionScoreEvaluatorService } from '../scores/competition-score-evaluator.service';


export class AmazonSvEvaluatorService {

	//** Configurations */
	private readonly PERCENTAGE_VARIANCE			: number = 20;
	private readonly AMAZON_SALES_FACTOR_NOVEMBER	: number = 1.4;
	private readonly AMAZON_SALES_FACTOR_DECEMBER	: number = 1.8;
	private readonly YEARLY_DECREASE				: number = 0.8;


	constructor(
		private googleSvEvaluatorService			: GoogleSvEvaluatorService,
		private searchVolumeEvaluatorHelper			: SearchVolumeEvaluatorHelper,
		private competitionScoreEvaluatorService	: CompetitionScoreEvaluatorService
	) {}


	/**------------------------------------------------------
	 * Search Volume & Trends
	 */
	current(keywordData: IAmazonCompositeKeywordData): number {
		const estAmazonSv: IGoogleSearchVolumeValue[] = this.computeSearchVolume(keywordData);
		return this.searchVolumeEvaluatorHelper.current(estAmazonSv);
	}

	history(keywordData: IAmazonCompositeKeywordData, monthDuration: number = 12): IGoogleSearchVolumeValue[] {
		const estAmazonSv: IGoogleSearchVolumeValue[] = this.computeSearchVolume(keywordData);
		return this.searchVolumeEvaluatorHelper.history(estAmazonSv, monthDuration);
	}


	/**------------------------------------------------------
	 * Estimate Amazon Sv Helpers
	 */
	private computeSearchVolume(keywordData: IAmazonCompositeKeywordData): IGoogleSearchVolumeValue[] {

		//0 - get estimated search volume (from competitiveness and avg reviews)
		const avgReviews	 	: number = keywordData.amazon.competition.reviews.count.avg;
		const productInterest	: number = keywordData.amazon.competition.totalProducts + avgReviews * 2;

		//1 - add the suggestion factor
		const suggestionsCount 	: number = keywordData.amazon.suggestions.length || 1;
		const estYearlyInterest	: number = productInterest * (suggestionsCount / 50);
		const estMonthlyInterest: number = estYearlyInterest / 12;

		//2 - do we have any Sv data?
		const isGoogleSvEmpty: boolean = Util.Array.isEmpty(keywordData.google?.searchVolume?.history);
		const isGoogleSvNull : boolean = keywordData.google?.searchVolume?.history.every((elem: IGoogleSearchVolumeValue) => elem.volume === 0);
		if (isGoogleSvEmpty || isGoogleSvNull) return [];

		//4 - return the computed amazon Sv
		const estAmazonSvHistory: IGoogleSearchVolumeValue[] = this.overlapSearchVolumeHistoryByRatio(keywordData.google.searchVolume.history, estMonthlyInterest);

		//5 - add simulation for christmas sales
		this.increaseChristmasVolume(estAmazonSvHistory);
		return estAmazonSvHistory;
	}

	private increaseChristmasVolume(history: IGoogleSearchVolumeValue[]) {

		//0 - increase the november and december
		const interpolatedGoogleSvHistory: IGoogleSearchVolumeValue[] = this.searchVolumeEvaluatorHelper.history(history, 11);
		const novemberSv = interpolatedGoogleSvHistory.find((elem: IGoogleSearchVolumeValue) => elem.month === 10);
		const decemberSv = interpolatedGoogleSvHistory.find((elem: IGoogleSearchVolumeValue) => elem.month === 11);
		if (novemberSv) novemberSv.volume = novemberSv.volume * this.AMAZON_SALES_FACTOR_NOVEMBER;
		if (decemberSv) decemberSv.volume = decemberSv.volume * this.AMAZON_SALES_FACTOR_DECEMBER;

		//1 - fake the values to make them look different
		for (const elem of interpolatedGoogleSvHistory) {

			//a. decrease the year a bit (because christmas makes it higher)
			elem.volume = elem.volume * this.YEARLY_DECREASE;

			//b. obfuscate the value to make the chart look different
			elem.volume = Util.Crypto.obfuscateBy20Percent(elem.volume);
		}

		//2 - return the result
		return interpolatedGoogleSvHistory;
	}

	private overlapSearchVolumeHistoryByRatio(googleSvHistory: IGoogleSearchVolumeValue[], estInterest: number): IGoogleSearchVolumeValue[] {

		//0 - get the avg sv value, used for the ratio
		const interpolatedGoogleSvHistory: IGoogleSearchVolumeValue[] = this.searchVolumeEvaluatorHelper.history(googleSvHistory, 11);
		const avgGoogleSvHistory: number = Stats.Basic.average(interpolatedGoogleSvHistory.map((elem: IGoogleSearchVolumeValue) => elem?.volume || 0));

		//1 - overlay the interest on the search volume
		const estAmazonSvHistory: IGoogleSearchVolumeValue[] = [];
		for (let index: number = 0; index < 12; index++) {

			//a. calculate the search volume for the month
			const legacyValueSv  : number = Util.Number.toSaveNumber(interpolatedGoogleSvHistory[index].volume, 1, Number.MAX_SAFE_INTEGER);
			const monthRatioToAvg: number = legacyValueSv / avgGoogleSvHistory;
			const estMonthSv	 : number = (estInterest + avgGoogleSvHistory * 0.2) * monthRatioToAvg;

			//b. add month to yearly search volume
			estAmazonSvHistory.push({
				month	: interpolatedGoogleSvHistory[index].month,
				year	: interpolatedGoogleSvHistory[index].year,
				volume	: estMonthSv
			});
		}

		//2 - return estimated sv
		return estAmazonSvHistory;
	}


	/**------------------------------------------------------
	 * Cmp & Cpc
	 */
	cmp(keywordData: IAmazonCompositeKeywordData): number | null {

		//0 - get the google cmp
		const googleCmp = this.googleSvEvaluatorService.cmp(keywordData);
		if (!googleCmp) return null;

		//1 - compute est amazon cmp
		const competitive : number = this.competitionScoreEvaluatorService.competitiveness(keywordData) * 100;
		const estAmazonCmp: number = googleCmp * 0.4 + competitive * 0.6;

		//2 - obfuscate the value
		const obfuscatedCmp: number = Util.Number.roundDouble(Util.Number.toSaveNumber(Util.Crypto.obfuscateBy10Percent(estAmazonCmp), 0, 100));
		return obfuscatedCmp;
	}

	cpc(keywordData: IAmazonCompositeKeywordData): IAmazonCmpEvaluation | null {

		//0 - estimate the cpc
		const estAmazonCpc = this.cpcAvg(keywordData);
		if (!estAmazonCpc) return null;

		//1 - give it in a range
		const amazonCpc: IAmazonCmpEvaluation = {
			max: Util.Number.roundDouble(estAmazonCpc + estAmazonCpc * this.PERCENTAGE_VARIANCE / 100),
			min: Util.Number.roundDouble(estAmazonCpc - estAmazonCpc * this.PERCENTAGE_VARIANCE / 100)
		};
		return amazonCpc;
	}


	/**------------------------------------------------------
	 * Sub Value Helpers
	 */
	cpcAvg(keywordData: IAmazonCompositeKeywordData): number | null {

		//0 - estimate the cpc
		const googleCpc: number = this.googleSvEvaluatorService.cpc(keywordData);
		if (!googleCpc || googleCpc === -1) return null;

		const profitMargin: number = keywordData.amazon.competition.price.avg / 10;
		const maxClickPay : number = profitMargin / 10;
		const objectiveCpc: number = googleCpc * 0.1 + maxClickPay * 0.9;

		//1 - add the ad competition as a factor
		const estAmazonCmp: number = (this.cmp(keywordData) ?? 0) / 100;
		const estAmazonCpc: number = objectiveCpc * 0.4 + (objectiveCpc * estAmazonCmp * 0.6);

		//2 - return the value
		return estAmazonCpc;
	}
}


//** Interfaces --------------------------------- */
export interface IAmazonCmpEvaluation {
	min		: number;
	max		: number;
}
